import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { affiliatesAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { btnGhost, btnPrimary, EmptyState, Field, inputCls, Modal, panelCls, StatusBadge } from '../../components/ui';

function EditModal({ item, plans, onClose, onSaved }) {
  const [status, setStatus] = useState(item.status);
  const [cashEligible, setCashEligible] = useState(Boolean(item.cash_eligible));
  const [planId, setPlanId] = useState(item.plan_id || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await affiliatesAPI.update(item.member_mid, {
        status, cash_eligible: cashEligible, plan_id: planId,
      });
      toast.success(t('Saved'));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`${item.member?.name || item.code}`} onClose={onClose}>
      <div className="space-y-4">
        <Field label={t('Status')}>
          <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">{t('active')}</option>
            <option value="suspended">{t('suspended')}</option>
          </select>
        </Field>
        <Field label={t('Commission plan')}>
          <select className={inputCls} value={planId} onChange={(e) => setPlanId(e.target.value)}>
            <option value="">{t('Base plan')}</option>
            {plans.filter((p) => !p.is_base).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-mms-muted">
          <input type="checkbox" checked={cashEligible} onChange={(e) => setCashEligible(e.target.checked)} />
          {t('Cash eligible (tax status verified)')}
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className={btnGhost}>{t('Cancel')}</button>
        <button onClick={save} disabled={busy} className={btnPrimary}>{busy ? t('Saving…') : t('Save')}</button>
      </div>
    </Modal>
  );
}

export default function AffiliatesPage() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    affiliatesAPI.list({ q }).then(({ data }) => setData(data))
      .catch(() => toast.error(t('Could not load affiliates')));
  }, [q]);
  useEffect(load, [load]);
  useEffect(() => {
    affiliatesAPI.plans().then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  return (
    <div>
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-mms-text">{t('Affiliates')}</h1>
          <p className="mt-1 text-[12.5px] text-mms-muted">{t('Profiles appear on first click, referral or portal visit.')}</p>
        </div>
        <input className={`${inputCls} !w-64`} placeholder={t('Search code…')} value={q}
          onChange={(e) => setQ(e.target.value)} />
      </div>
      {data === null ? (
        <p className="text-sm text-mms-muted">{t('Loading…')}</p>
      ) : data.items.length === 0 ? (
        <EmptyState>{t('No affiliate profiles yet — they appear on first click, referral or portal visit.')}</EmptyState>
      ) : (
        <div className={`${panelCls} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-mms-border text-left text-xs text-mms-faint">
                <th className="px-4 py-2.5 font-medium">{t('Member')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Code')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('Clicks')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('Referrals')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('Revenue')}</th>
                <th className="px-4 py-2.5 font-medium text-right">{t('Cash available')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Status')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((a) => (
                <tr key={a.member_mid} className="border-b border-mms-border/60 last:border-0">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-mms-raised text-[10px] font-semibold" style={{ color: 'var(--mms-sidebar)' }}>
                        {String(a.member?.name || a.code || '?').trim().slice(0, 2).toUpperCase()}
                      </span>
                      <span className="text-mms-text">{a.member?.name || '—'}</span>
                      {a.cash_eligible ? <span className="text-xs text-[var(--mms-success)]">$</span> : null}
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><code className="rounded bg-mms-raised px-1.5 py-0.5 text-xs text-mms-muted">{a.code}</code></td>
                  <td className="px-4 py-2.5 text-right mms-num">{a.stats?.clicks}</td>
                  <td className="px-4 py-2.5 text-right mms-num">{a.stats?.referrals}</td>
                  <td className="px-4 py-2.5 text-right mms-num">{a.stats?.revenue}</td>
                  <td className="px-4 py-2.5 text-right mms-num">{a.balance?.available}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={a.status} /></td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => setEditing(a)} className={btnGhost}>{t('Edit')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <EditModal item={editing} plans={plans}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
